import useLocale from '@/hooks/useLocale';
import { ShopifyVariant } from '@/lib/shopify';
import { getLanguageFromLocale } from '@/utils/translations/localizationHelpers';
import { Language } from '@/utils/translations/types/TypeLanguagesAndLocales';
import { memo } from 'react';

export interface OfferSchemaProps {
  variant: ShopifyVariant;
  url: string;
  isPlayKit?: boolean;
}

function _getStandaloneNameLocalized(language: Language) {
  switch (language) {
    case Language.English:
      return 'Single Play Kit Purchase (no subscription)';
    case Language.French:
      return 'Achat unique d’un Kit d’éveil (sans abonnement)';
    case Language.Dutch:
      return 'Eenmalige aankoop van een Speelkit (zonder abonnement)';
    case Language.Spanish:
      return 'Compra única de un Kit de Juego (sin suscripción)';
    case Language.German:
      return 'Einmaliger Kauf eines Spielzeugsets (kein Abo)';
    default:
      throw new Error('missing translation');
  }
}

function _getSubscriptionNameLocalized(language: Language) {
  switch (language) {
    case Language.English:
      return 'Play Kit Subscription (Pay Per Kit)';
    case Language.French:
      return 'Abonnement aux Kits d’éveil (Paiement à la livraison)';
    case Language.Dutch:
      return 'Abonnement op de Speelkits (Betaling per levering)';
    case Language.Spanish:
      return 'Suscripción a los Kits de Juego (Pago por kit)';
    case Language.German:
      return 'Abo für die Spielzeugsets (Bezahlung pro Set)';
    default:
      throw new Error('missing translation');
  }
}

function _formatPrice(price: string | number | undefined) {
  if (!price) return '0.00';
  const priceNum = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(priceNum)) return '0.00';
  return priceNum.toFixed(2);
}

const OfferSchema = ({ variant, url, isPlayKit }: OfferSchemaProps) => {
  const availability = variant.availableForSale
    ? 'http://schema.org/InStock'
    : 'http://schema.org/OutOfStock';

  const locale = useLocale();
  const language = getLanguageFromLocale(locale);

  const variantPrice = _formatPrice(variant.price?.amount);
  const variantCurrency = variant.price?.currencyCode;

  const hasStandalonePrice =
    typeof variant.priceCentsStandalone !== 'undefined';

  if (isPlayKit && hasStandalonePrice) {
    const standalonePrice = _formatPrice(
      variant.priceCentsStandalone ? variant.priceCentsStandalone / 100 : 0,
    );
    const standaloneName = _getStandaloneNameLocalized(language);
    const subscriptionName = _getSubscriptionNameLocalized(language);
    return (
      <>
        <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
          <meta itemProp="name" content={standaloneName} />
          <meta itemProp="price" content={standalonePrice} />
          <meta itemProp="priceCurrency" content={variantCurrency} />
          <meta itemProp="url" content={url} />
          <link itemProp="availability" href={availability} />
          <meta
            itemProp="itemCondition"
            content="https://schema.org/NewCondition"
          />
        </div>
        <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
          <meta itemProp="name" content={subscriptionName} />
          <meta itemProp="price" content={variantPrice} />
          <meta itemProp="priceCurrency" content={variantCurrency} />
          <meta itemProp="url" content={url} />
          <link itemProp="availability" href={availability} />
          <meta
            itemProp="itemCondition"
            content="https://schema.org/NewCondition"
          />
        </div>
      </>
    );
  }

  return (
    <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
      <meta itemProp="price" content={variantPrice} />
      <meta itemProp="priceCurrency" content={variantCurrency} />
      <meta itemProp="url" content={url} />
      <link itemProp="availability" href={availability} />
    </div>
  );
};

export default memo(OfferSchema);
